/**
 * Navbar - Barra de navegación superior
 * Muestra el título, usuario logueado y opciones de perfil
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Avatar,
  Chip,
  Divider,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Logout,
  Person,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

/**
 * @component Navbar
 * @description Barra superior con título, usuario y menú de perfil
 * @param {Object} props
 * @param {Function} props.onToggleSidebar - Función para toggle del sidebar
 * @param {number} props.drawerWidth - Ancho del drawer
 * @param {boolean} props.sidebarOpen - Estado del sidebar
 */
const Navbar = ({ onToggleSidebar, drawerWidth, sidebarOpen }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      ADMIN: 'error',
      SUPERVISOR: 'warning',
      OPERADOR: 'info',
      CONSULTOR: 'success',
    };
    return colors[role] || 'default';
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        transition: (theme) =>
          theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
      }}
    >
      <Toolbar>
        {/* Botón de menú */}
        <IconButton
          color="inherit"
          edge="start"
          onClick={onToggleSidebar}
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>

        {/* Título de la aplicación */}
        <Typography
          variant="h6"
          noWrap
          component="div"
          sx={{
            flexGrow: 1,
            fontWeight: 600,
            display: { xs: 'none', sm: 'block' }
          }}
        >
          Sistema de Gestión de Cheques
        </Typography>

        {/* Título abreviado en móvil */}
        <Typography
          variant="h6"
          noWrap
          component="div"
          sx={{
            flexGrow: 1,
            fontWeight: 600,
            display: { xs: 'block', sm: 'none' }
          }}
        >
          Cheques
        </Typography>

        {/* Usuario y menú */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Nombre del usuario - oculto en móvil */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'flex-end', mr: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {user?.nombre} {user?.apellido}
            </Typography>
            <Chip
              label={user?.rol}
              size="small"
              color={getRoleBadgeColor(user?.rol)}
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          </Box>

          {/* Avatar y menú */}
          <IconButton
            onClick={handleMenuOpen}
            color="inherit"
            sx={{
              p: 0.5,
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: 'primary.light',
              }}
            >
              {user?.nombre?.[0]}{user?.apellido?.[0]}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              elevation: 3,
              sx: {
                mt: 1.5,
                minWidth: 220,
              },
            }}
          >
            {/* Información del usuario */}
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {user?.nombre} {user?.apellido}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                {user?.email}
              </Typography>
              <Chip
                label={user?.rol}
                size="small"
                color={getRoleBadgeColor(user?.rol)}
                sx={{ mt: 1, height: 22, fontSize: '0.75rem' }}
              />
            </Box>

            <Divider />

            {/* Opciones del menú */}
            <MenuItem onClick={() => { handleMenuClose(); navigate('/perfil'); }}>
              <ListItemIcon>
                <Person fontSize="small" />
              </ListItemIcon>
              <ListItemText>Mi Perfil</ListItemText>
            </MenuItem>

            <Divider />

            <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <Logout fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Cerrar Sesión</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

Navbar.propTypes = {
  onToggleSidebar: PropTypes.func.isRequired,
  drawerWidth: PropTypes.number.isRequired,
  sidebarOpen: PropTypes.bool.isRequired,
};

export default Navbar;
